import { useState, useEffect } from "react";
import { Pen, Trash2, Info, Lock, Plus, AlertTriangle } from "lucide-react";
import { FaFileWord } from "react-icons/fa";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as Popover from "@radix-ui/react-popover";
import AddWordAlert from "./AddWordAlert";

// Custom Delete Confirmation Modal
const DeleteConfirmationModal = ({
  show,
  onClose,
  onConfirm,
  isDeleting,
  ruleToDelete,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl sm:ml-[130px] w-[90%] sm:w-[40%] xl:w-[30%] 2xl:w-[25%] shadow-xl relative">
        <div className="text-[18px] flex py-2 px-4 border-b border-b-[#DDDDDD] sticky z-10 top-0 bg-white">
          CONFIRM DELETE
        </div>
        <div className="p-3 flex flex-col gap-3">
          <div className="flex flex-col items-center gap-2 bg-[#FFF5F5] border border-[#FFE6E6] p-3 rounded">
            <div className="flex flex-row items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <div className="font-semibold text-[16px] text-red-700">
                Delete Word Alert
              </div>
            </div>
            <div className="text-[14px] ">
              Are you sure you want to delete the word{" "}
              <span className="font-semibold">{ruleToDelete?.word}</span> as a{" "}
              <span className="font-semibold">
                {ruleToDelete?.scope === "default"
                  ? "default"
                  : ruleToDelete?.scope === "district"
                    ? "district-wide"
                    : ruleToDelete?.scope === "school"
                      ? "school-wide"
                      : ruleToDelete?.scope === "custom"
                        ? "custom"
                        : ""}
              </span>{" "}
              <span className="font-semibold">{`tier ${ruleToDelete?.tier}`}</span>{" "}
              word alert? This action cannot be undone.
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="py-2 px-3 border border-gray-300 font-semibold rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="py-2 px-3 bg-red-600 text-white font-semibold rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WordAlertSettings = ({
  data,
  isLoading,
  updateWordMutation,
  deleteWordMutation,
  createWordsMutation,
  user,
  schoolId,
  districtId,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [onMobile, setOnMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setOnMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let allRules = [];
  if (data && data.byScope) {
    if (Array.isArray(data.byScope.default)) {
      allRules = allRules.concat(
        data.byScope.default.map((rule) => ({
          ...rule,
          scopeLabel: "Default",
        })),
      );
    }
    if (Array.isArray(data.byScope.district)) {
      allRules = allRules.concat(
        data.byScope.district.map((rule) => ({
          ...rule,
          scopeLabel: "District-wide",
        })),
      );
    }
    if (Array.isArray(data.byScope.school)) {
      allRules = allRules.concat(
        data.byScope.school.map((rule) => ({
          ...rule,
          scopeLabel: "School-wide",
        })),
      );
    }
    if (Array.isArray(data.byScope.custom)) {
      allRules = allRules.concat(
        data.byScope.custom.map((rule) => ({
          ...rule,
          scopeLabel: "Custom (Visible to me only)",
        })),
      );
    }
  }

  allRules = allRules.sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  function canModifyRule(userData, setting) {
    const roleOrder = ["123wellness", "superadmin", "admin", "teacher"];
    const scopeOrder = ["default", "district", "school", "custom"];
    const userRoleIndex = roleOrder.indexOf(userData.role);
    const settingScopeIndex = scopeOrder.indexOf(setting.scope);

    if (userRoleIndex === -1) return { canEdit: false, canDelete: false };

    if (
      setting.scope === "custom" &&
      setting.createdBy.toString() !== userData._id.toString()
    ) {
      return { canEdit: false, canDelete: false };
    }

    if (userData.role !== "123wellness") {
      if (setting.createdBy.toString() !== userData._id.toString()) {
        // ensures that shared settings (like those at the "school" scope) are protected unless the user is the creator, higher authority (e.g., "123wellness")
        // or of the same scope
        if (settingScopeIndex < userRoleIndex) {
          return { canEdit: false, canDelete: false };
        }
      }
    }

    return { canEdit: true, canDelete: true };
  }

  const handleEdit = async (rule) => {
    try {
      // Edit functionality would use updateWordMutation.mutateAsync(rule._id, updates)
      // This is compatible with the new hook API
      alert("Edit functionality coming soon.");
    } catch (error) {
      alert("Failed to edit word. Please try again.");
    }
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;

    try {
      await deleteWordMutation.mutateAsync(ruleToDelete._id);
      setShowDeleteModal(false);
      setRuleToDelete(null);
      // Success feedback could be added here
    } catch (error) {
      alert("Failed to delete word. Please try again.");
      // Keep modal open on error so user can try again
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRuleToDelete(null);
  };

  const sortedRules = (allRules || []).slice().sort((a, b) => {
    const getTime = (r) => {
      if (r?.createdAt) {
        const t = new Date(r.createdAt).getTime();
        if (!Number.isNaN(t)) return t;
      }
      if (r?.updatedAt) {
        const t = new Date(r.updatedAt).getTime();
        if (!Number.isNaN(t)) return t;
      }
      return 0;
    };

    const timeDiff = getTime(b) - getTime(a);
    if (timeDiff !== 0) return timeDiff; // Newest first

    // Tie-breaker: keep previous tier ordering
    const tierOrder = { tier3: 0, tier2: 1, 3: 0, 2: 1 };
    const aTier = tierOrder[a.tier] !== undefined ? tierOrder[a.tier] : 2;
    const bTier = tierOrder[b.tier] !== undefined ? tierOrder[b.tier] : 2;
    return aTier - bTier;
  });

  return (
    <div className=" bg-white rounded flex flex-col p-3 w-full">
      {!onMobile && (
        <div className="flex flex-row gap-2 pb-2.5 items-center">
          <FaFileWord size={14} />
          WORD ALERTS
          <div className="group">
            <Info size={14} className="text-[#256AA6BF]" />
            <span className="absolute z-10 hidden group-hover:block mt-1 py-2 px-3 rounded shadow bg-white text-[16px] w-[30%]">
              Word alerts empower you to decide when you will get “alerted”
              if/when students types a certain word while using the application.
              For example, you may want to know if a student types “hurt” in the
              “other” column when choosing their wellness practice. In your
              Student Supports page, students who type a word that you’ve set as
              a “word alert” will appear in yellow (tier 2) or red (tier 3).
              Certain words, like ”gun” are set as default to help get you
              started. Administrators can add “schoolwide” word alerts anytime.
              All educators can also add custom word alerts (only visible to
              you). Bottom line, word alerts empower you, a talented and caring
              educator, to be even more in-tuned to when your students may
              benefit from support!
            </span>
          </div>
        </div>
      )}

      {onMobile ? (
        <div className="w-full relative">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mb-2.5"></div>
              <div className="text-[16px] text-gray-500">
                Loading word alerts...
              </div>
            </div>
          ) : sortedRules.length > 0 ? (
            <div className="max-h-[50vh] overflow-y-auto pb-4 space-y-3 relative">
              {sortedRules.map((rule, idx) => {
                const can = canModifyRule(user, rule);
                return (
                  <div
                    key={`${rule.word}-${rule.tier}-${idx}`}
                    className={`border border-[#DDDDDD] rounded p-3 bg-white`}
                  >
                    <div className="grid grid-cols-2 gap-y-3">
                      <div className="opacity-50 text-sm">WORD</div>
                      <div className="capitalize text-base">{rule.word}</div>
                      <div className="opacity-50 text-sm">TIER</div>
                      <div className="text-base">{`T${rule.tier}`}</div>
                      <div className="opacity-50 text-sm">RULE TYPE</div>
                      <div className="text-base">
                        {rule.scope === "default" && "Default"}
                        {rule.scope === "district" && "District-wide"}
                        {rule.scope === "school" && "School-wide"}
                        {rule.scope === "custom" && "Custom (Visible to me)"}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      {can.canEdit || can.canDelete ? (
                        <>
                          {can.canEdit && (
                            <Popover.Root>
                              <Popover.Trigger asChild>
                                <button
                                  className="flex items-center gap-2 px-4 py-2 rounded text-base text-[#0047CC] bg-[#D5E6FD] font-semibold"
                                  type="button"
                                >
                                  <Pen size={12} className="text-[#0047CC]" />
                                  Edit
                                </button>
                              </Popover.Trigger>
                              <Popover.Portal>
                                <Popover.Content
                                  side="top"
                                  align="center"
                                  sideOffset={6}
                                  className="bg-black flex-wrap justify-center opacity-90 text-white max-w-[80vw] mx-8 text-sm rounded py-2 px-3 z-50 shadow"
                                >
                                  Editing coming soon. Please feel free to edit
                                  by creating a new rule.
                                  <Popover.Arrow className="fill-black" />
                                </Popover.Content>
                              </Popover.Portal>
                            </Popover.Root>
                          )}
                          {can.canDelete && (
                            <button
                              className="flex items-center gap-2 px-4 py-2 rounded text-base text-[#B50E1C] bg-[#FFDDDE] font-semibold"
                              onClick={() => handleDeleteClick(rule)}
                            >
                              <Trash2 size={12} className="text-[#B50E1C]" />
                              Delete
                            </button>
                          )}
                        </>
                      ) : (
                        <Popover.Root>
                          <Popover.Trigger asChild>
                            <button type="button">
                              <Lock size={20} className="text-gray-400" />
                            </button>
                          </Popover.Trigger>
                          <Popover.Portal>
                            <Popover.Content
                              side="top"
                              align="center"
                              sideOffset={6}
                              className="bg-black opacity-90 text-white text-sm rounded py-2 px-3 z-50 shadow flex-wrap max-w-[80vw] mx-8"
                            >
                              Please use the “Add Alert” button to create a new
                              alert.
                              <Popover.Arrow className="fill-black" />
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-5">
              <div className="text-[16px] text-gray-500">
                No word alerts found. Add your first alert below.
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-4 pb-2.5 w-full bg-[#F8FAFB80] border border-[#DDDDDD] rounded-tl rounded-tr max-h-[40vh] overflow-y-scroll">
          {isLoading ? (
            <div className="col-span-4 flex flex-col items-center justify-center py-5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mb-2.5"></div>
              <div className="text-[16px] text-gray-500">
                Loading word alerts...
              </div>
            </div>
          ) : sortedRules.length > 0 ? (
            <>
              <div
                className="col-span-4 grid grid-cols-4 px-8 py-3 font-semibold text-[12px] uppercase
            border-b border-b-[#DDDDDD] sticky top-0 bg-[white]"
              >
                <div>Word</div>
                <div>Tier</div>
                <div>Rule Type</div>
                <div className="text-right">Actions</div>
              </div>
              {sortedRules.map((rule, idx) => {
                const { canEdit, canDelete } = canModifyRule(user, rule);

                // Get tooltip message based on rule type and permissions
                const getTooltipMessage = (rule) => {
                  if (rule.scope === "default") {
                    return user.role === "123wellness"
                      ? "Edit default rule"
                      : "Only 123wellness can edit default rules";
                  }
                  if (rule.scope === "district") {
                    return rule.role === "superadmin"
                      ? rule.entityId === districtId?.toString()
                        ? "Edit district rule"
                        : "You can only edit rules for your district"
                      : "Only superadmins can edit district rules";
                  }
                  if (rule.scope === "school") {
                    return rule.role === "admin"
                      ? rule.entityId === schoolId?.toString()
                        ? "Edit school rule"
                        : "You can only edit rules for your school"
                      : "Only admins can edit school rules.";
                  }
                  if (rule.scope === "custom") {
                    return rule.createdBy?.toString() === user?._id.toString()
                      ? "Edit custom rule"
                      : "You can only edit your own custom rules";
                  }
                  return "You do not have permission to edit this rule";
                };

                return (
                  <div
                    key={`${rule.word}-${rule.tier}-${idx}`}
                    className="col-span-4 grid grid-cols-4 items-center px-8 py-3 text-[16px] border-b border-b-[#DDDDDD]"
                  >
                    <div className="capitalize flex flex-row gap-2">
                      {rule.word}
                    </div>
                    <div>{`T${rule.tier}`}</div>
                    <div>
                      {rule.scope === "default" && "Default"}
                      {rule.scope === "district" && "District-wide"}
                      {rule.scope === "school" && "School-wide"}
                      {rule.scope === "custom" && "Custom (Visible to me)"}
                    </div>

                    <div className="flex justify-end gap-2">
                      <Tooltip.Provider delayDuration={300}>
                        {!canEdit && !canDelete ? (
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Lock
                                size={16}
                                className="text-gray-400 cursor-not-allowed"
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side="top"
                                align="end"
                                sideOffset={6}
                                className="bg-black opacity-90 text-white text-[16px] rounded py-2 px-3 z-50 shadow"
                              >
                                {getTooltipMessage(rule)}
                                <Tooltip.Arrow className="fill-black" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        ) : (
                          <>
                            {canEdit && (
                              <Popover.Root>
                                <Popover.Trigger asChild>
                                  <span>
                                    <Pen
                                      size={16}
                                      className={`text-gray-600 hover:text-blue-600 cursor-pointer ${
                                        updateWordMutation.isLoading
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                    />
                                  </span>
                                </Popover.Trigger>
                                <Popover.Portal>
                                  <Popover.Content
                                    side="top"
                                    align="end"
                                    sideOffset={6}
                                    className="bg-black opacity-90 text-white  text-[16px] rounded py-2 px-3 z-50 shadow"
                                  >
                                    Editing coming soon. Please feel free to
                                    edit by creating a new rule.
                                    <Popover.Arrow className="fill-black" />
                                  </Popover.Content>
                                </Popover.Portal>
                              </Popover.Root>
                            )}

                            {/* Delete icon */}
                            {canDelete && (
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <Trash2
                                    size={16}
                                    className={`text-red-500 hover:text-red-700 cursor-pointer ${
                                      deleteWordMutation.isLoading
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      !deleteWordMutation.isLoading &&
                                      handleDeleteClick(rule)
                                    }
                                  />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <Tooltip.Content
                                    side="top"
                                    align="end"
                                    sideOffset={6}
                                    className="bg-black opacity-90 text-white text-[16px] rounded py-2 px-3 z-50 shadow"
                                  >
                                    Delete word alert
                                    <Tooltip.Arrow className="fill-black" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            )}
                          </>
                        )}
                      </Tooltip.Provider>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="col-span-4 flex items-center justify-center py-8">
              <div className="text-[16px] text-gray-500">
                No word alerts found. Add your first alert below.
              </div>
            </div>
          )}
        </div>
      )}
      {showPopup && (
        <AddWordAlert
          show={showPopup}
          onClose={() => setShowPopup(false)}
          data={allRules}
          createWordsMutation={createWordsMutation}
          user={user}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          show={showDeleteModal}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          ruleToDelete={ruleToDelete}
          isDeleting={deleteWordMutation.isPending}
        />
      )}
      <div className="w-full flex justify-center">
        <button
          onClick={() => setShowPopup(true)}
          className="flex items-center justify-center gap-1 bg-[#256AA6BF] text-white py-3 w-full
          rounded-bl rounded-br transition font-bold text-[14px] hover:opacity-90"
        >
          <Plus size={15} />
          Add Alert
        </button>
      </div>
    </div>
  );
};
export default WordAlertSettings;
